import { supabase } from '../../../shared/lib/supabase';

// Define the Player interface based on actual database schema
export interface Player {
  idx: number;
  id: string;
  full_name: string;
  date_of_birth: string | null;
  nationality: string | null;
  profile_image: string | null;
  position: string | null;
  height_cm: number | null;
  weight_kg: number | null;
  preferred_foot: string | null;
  kit_number: number | null;
  bio: string | null;
  social_media: any | null;
  contact_info: any | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  data_source: string;
}

export interface PlayerCreateInput {
  full_name: string;
  date_of_birth?: string | null;
  nationality?: string | null;
  profile_image?: string | null;
  position?: string | null;
  height_cm?: number | null;
  weight_kg?: number | null;
  preferred_foot?: string | null;
  kit_number?: number | null;
  bio?: string | null;
  social_media?: any | null;
  contact_info?: any | null;
  is_active?: boolean;
  data_source: string;
}

export interface PlayerUpdateInput {
  full_name?: string;
  date_of_birth?: string | null;
  nationality?: string | null;
  profile_image?: string | null;
  position?: string | null;
  height_cm?: number | null;
  weight_kg?: number | null;
  preferred_foot?: string | null;
  kit_number?: number | null;
  bio?: string | null;
  social_media?: any | null;
  contact_info?: any | null;
  is_active?: boolean;
  data_source?: string;
}

class PlayersService {
  async getAll(): Promise<Player[]> {
    try {
      console.log('🔍 Fetching players...');
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching players:', error);
        throw error;
      }

      console.log('✅ Players fetched successfully:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('Error in playersService.getAll:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<Player | null> {
    try {
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching player:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in playersService.getById:', error);
      throw error;
    }
  }

  async create(player: PlayerCreateInput): Promise<Player> {
    try {
      console.log('🔍 Creating player:', player);
      const { data, error } = await supabase
        .from('players')
        .insert([player])
        .select()
        .single();

      if (error) {
        console.error('Error creating player:', error);
        throw error;
      }

      console.log('✅ Player created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in playersService.create:', error);
      throw error;
    }
  }

  async update(id: string, player: PlayerUpdateInput): Promise<Player> {
    try {
      console.log('🔍 Updating player:', id, player);
      const { data, error } = await supabase
        .from('players')
        .update(player)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating player:', error);
        throw error;
      }

      console.log('✅ Player updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in playersService.update:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log('🔍 Deleting player:', id);
      const { error } = await supabase
        .from('players')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting player:', error);
        throw error;
      }

      console.log('✅ Player deleted successfully');
    } catch (error) {
      console.error('Error in playersService.delete:', error);
      throw error;
    }
  }
}

export const playersService = new PlayersService();
